import React from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import { ChevronRightIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { Await } from '@oysterjs/ui/async';
import { Badge } from '@oysterjs/ui/badge';
import { PageTitle } from '@oysterjs/ui/page';

import type {
  SmartFillFormBucketFieldsFragment,
  SmartFillCarrierSiteFieldsFragment
} from '~/api/graphql';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { bucketSources, bucketTitle, carrierSiteName } from '~/components/smart-fill/form-buckets';
import { formatRelativeTime, rowDateTimeFormatter } from '~/components/table-formatters';
import { pluralize } from '~/lib/pluralize';

type SmartFillBucketsLoaderData = {
  formBuckets?: SmartFillFormBucketFieldsFragment[] | null;
  carrierSites?: SmartFillCarrierSiteFieldsFragment[] | null;
};

// Cara builds a bucket when a run finishes, which may be while this list is open.
const REFRESH_MS = 10_000;

/** Re-reads the list on an interval, and on returning to the tab, while it is on screen. */
const useRefreshWhileVisible = (intervalMs: number) => {
  const revalidator = useRevalidator();
  React.useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible' && revalidator.state === 'idle') {
        revalidator.revalidate();
      }
    };
    const interval = window.setInterval(refresh, intervalMs);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [revalidator, intervalMs]);
};

const Separator = () => (
  <span aria-hidden="true" className="text-neutral-300 dark:text-neutral-600">
    ·
  </span>
);

const FormBucketRow = ({
  formBucket,
  carrierSites
}: {
  formBucket: SmartFillFormBucketFieldsFragment;
  carrierSites: SmartFillCarrierSiteFieldsFragment[];
}) => {
  const builtAt = new Date(formBucket.builtAt);
  const configuredSite = carrierSites.find((site) => site.id === formBucket.carrierSiteId);
  const answers = formBucket.answerCount;

  return (
    <li className="relative flex items-center gap-4 px-4 py-4 hover:bg-neutral-50 focus-within:bg-neutral-50 dark:hover:bg-white/5 dark:focus-within:bg-white/5 sm:px-6">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
        <ClipboardDocumentCheckIcon className="size-5" />
      </div>
      <div className="min-w-0 flex-auto">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="truncate text-sm/6 font-semibold text-neutral-900 dark:text-white">
            <Link to={`/app/smart-fill/${formBucket.bucket.id}`} className="focus:outline-none">
              {/* Stretched over the row so all of it opens the bucket. */}
              <span aria-hidden="true" className="absolute inset-0" />
              {bucketTitle(formBucket)}
            </Link>
          </p>
          <Badge color="sky">{formBucket.lineOfBusiness}</Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs/5 text-neutral-500 dark:text-neutral-400">
          <Link
            to={`/app/workspaces/${formBucket.workspaceId}`}
            className="relative z-10 max-w-xs truncate font-medium text-neutral-600 hover:text-neutral-900 hover:underline dark:text-neutral-400 dark:hover:text-white"
          >
            {formBucket.workspaceTitle || 'Untitled workspace'}
          </Link>
          <Separator />
          <span>From {bucketSources(formBucket)}</span>
          <Separator />
          <time dateTime={builtAt.toISOString()} title={rowDateTimeFormatter.format(builtAt)}>
            Built {formatRelativeTime(builtAt)}
          </time>
          <Separator />
          <span>
            {answers > 0 ? `${answers} ${pluralize(answers, 'answer')} added` : 'No answers added'}
          </span>
        </div>
      </div>
      {configuredSite && (
        <Badge color="emerald" className="hidden shrink-0 sm:inline-flex">
          {carrierSiteName(configuredSite)}
        </Badge>
      )}
      <ChevronRightIcon
        aria-hidden="true"
        className="size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
      />
    </li>
  );
};

export const SmartFillBucketsPage = () => {
  const loader = useLoaderData() as { data: Promise<SmartFillBucketsLoaderData> } | undefined;
  useRefreshWhileVisible(REFRESH_MS);

  return (
    <PageTitle
      title="Smart Fill"
      description="One bucket for each line of business in a workspace, built from the ACORD forms Cara fills. Configure one for a carrier site to see what that site still needs."
    >
      <React.Suspense fallback={<LibraryListLoading title="Loading buckets..." />}>
        <Await resolve={loader?.data}>
          {({ formBuckets, carrierSites }) =>
            formBuckets?.length ? (
              <ul
                role="list"
                className="divide-y divide-neutral-100 overflow-hidden rounded-xl bg-white shadow ring-1 ring-neutral-950/5 dark:divide-neutral-700 dark:bg-neutral-800 dark:ring-white/10"
              >
                {formBuckets.map((formBucket) => (
                  <FormBucketRow
                    key={formBucket.bucket.id}
                    formBucket={formBucket}
                    carrierSites={carrierSites ?? []}
                  />
                ))}
              </ul>
            ) : (
              <LibraryListEmptyState
                icon={ClipboardDocumentCheckIcon}
                title="No buckets yet"
                description="When Cara fills an ACORD form, its line of business gets a bucket here. For a workspace without forms, choose Smart Fill bucket in the workspace's menu."
              />
            )
          }
        </Await>
      </React.Suspense>
    </PageTitle>
  );
};
